import { useEffect, useRef, useState } from "react";
import { LoadingPresentationBase, resolveLoadingPresentation } from "../utility/first-load-skeletons";

/**
 * How long a first load may take before its skeleton is drawn. Most designer list fetches answer
 * inside it, and for those the skeleton never appears — no flash of placeholder before the rows.
 */
export const LOADING_SKELETON_DELAY_MS: number = 200;

/**
 * Once drawn, a skeleton stays at least this long, so a load that ends just after the delay
 * does not blink the placeholder for a frame or two.
 */
export const LOADING_SKELETON_MIN_VISIBLE_MS: number = 500;

/**
 * `pending`: loading with nothing to show yet and the delay still running. Draw neither the
 * skeleton nor the empty state — keep the region's space and wait.
 */
export type LoadingPresentation = LoadingPresentationBase | "pending";

export interface IUseLoadingPresentationOptions {
  isLoading: boolean;
  /** Whether the region already has its own data on screen. */
  hasData: boolean;
  delayMs?: number;
  minVisibleMs?: number;
}

/**
 * What a loading region should draw right now: `resolveLoadingPresentation` (skeleton only while
 * the region has none of its own data; with data, `refreshing`) plus timing — a skeleton appears
 * only after `delayMs` of waiting and, once it appears, stays at least `minVisibleMs`.
 */
const useLoadingPresentation = ({
  isLoading,
  hasData,
  delayMs = LOADING_SKELETON_DELAY_MS,
  minVisibleMs = LOADING_SKELETON_MIN_VISIBLE_MS
}: IUseLoadingPresentationOptions): LoadingPresentation => {
  const base: LoadingPresentationBase = resolveLoadingPresentation(isLoading, hasData);
  const wantsSkeleton: boolean = base === "skeleton";
  const [isSkeletonShown, setIsSkeletonShown] = useState<boolean>(false);
  // Written by the timer that shows the skeleton, read only in the effect that hides it.
  const shownAtRef = useRef<number>(0);

  useEffect((): (() => void) | undefined => {
    if (wantsSkeleton && !isSkeletonShown) {
      const showTimer: ReturnType<typeof setTimeout> = setTimeout((): void => {
        shownAtRef.current = Date.now();
        setIsSkeletonShown(true);
      }, delayMs);
      return (): void => clearTimeout(showTimer);
    }
    if (!wantsSkeleton && isSkeletonShown) {
      const remainingMs: number = Math.max(minVisibleMs - (Date.now() - shownAtRef.current), 0);
      const hideTimer: ReturnType<typeof setTimeout> = setTimeout((): void => setIsSkeletonShown(false), remainingMs);
      return (): void => clearTimeout(hideTimer);
    }
    return undefined;
  }, [wantsSkeleton, isSkeletonShown, delayMs, minVisibleMs]);

  if (wantsSkeleton) {
    return isSkeletonShown ? "skeleton" : "pending";
  }
  // The load ended while the skeleton was on screen: hold it until its minimum has passed.
  if (isSkeletonShown) {
    return "skeleton";
  }
  return base;
};

export default useLoadingPresentation;
